/**
 * LeagueDashboardPage — /league/:leagueId/dashboard
 * ─────────────────────────────────────────────────────────────────
 * Overview of a single league: name, sport, current season summary,
 * member count, recent activity placeholders.
 *
 * Phase 2a content:
 *   • League header (name, role badge, status)
 *   • Member counts by role
 *   • "Next steps" panel guiding a new manager
 *     (since Phase 2a only ships shell + members, the dashboard
 *     hints at what to do next; later phases fill the empty slots)
 *
 * The page is visible to any league member. The data it surfaces
 * already excludes sensitive fields (no member emails, no
 * signature blobs, etc.); see backend GET /api/leagues/:id and
 * GET /api/leagues/:id/members.
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Users, Crown, Shield, Eye, Calendar, MapPin, Layers, Trophy, ArrowRight, Mail } from 'lucide-react';
import api from '../../services/api.js';
import toast from 'react-hot-toast';
import LeagueSubNav from './LeagueSubNav.jsx';
import { useLeagueMembership } from '../../hooks/useLeagueMembership.js';
// v2.5.124 — Phase 11 (date display).
import { formatDateRange } from '../../utils/formatDate.js';

export default function LeagueDashboardPage() {
  const { leagueId } = useParams();
  const { role, isManager, loading: roleLoading } = useLeagueMembership(leagueId);

  const [league,  setLeague]  = useState(null);
  const [members, setMembers] = useState([]);
  const [seasons, setSeasons] = useState([]);
  // v2.5.124 — Phase 11. The Next Steps panel previously hard-coded
  // every step's `available={false}` / `done={false}` from Phase 2a.
  // It's now accurate: we count venues, divisions, teams, games to
  // mark steps complete dynamically and show the right "Available
  // now — X tab" / "Done" sub-text. Counts are cheap reads and the
  // dashboard already does Promise.all, so adding 4 more requests
  // doesn't change perceived load time.
  const [progress, setProgress] = useState({ venues: 0, divisions: 0, teams: 0, games: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leagueId) return;
    let cancelled = false;
    // v2.5.130 — distinguish auth failures (401) from genuine
    // missing-league cases (404 / 403). The previous load chain
    // used `.catch(() => ({ data: null }))` everywhere, which
    // swallowed 401s. The page then rendered "League not found"
    // when the user was actually just logged out — wildly
    // misleading. The new pattern: track whether the primary
    // /leagues/:id call returned a 401, and if so, set
    // authFailed instead of leaving league=null. The api
    // interceptor handles the actual login redirect; we just
    // need to avoid the false "not found" UI in the brief
    // window before the redirect fires.
    let authFailedThisLoad = false;
    Promise.all([
      api.get(`/leagues/${leagueId}`).catch((e) => {
        if (e?.response?.status === 401) authFailedThisLoad = true;
        return { data: null };
      }),
      api.get(`/leagues/${leagueId}/members`).catch(() => ({ data: { members: [] } })),
      api.get(`/leagues/${leagueId}/seasons`).catch(() => ({ data: { seasons: [] } })),
      api.get(`/leagues/${leagueId}/venues`).catch(() => ({ data: { venues: [] } })),
    ]).then(([leagueRes, membersRes, seasonsRes, venuesRes]) => {
      if (cancelled) return;
      if (authFailedThisLoad) {
        // Don't render anything — the api interceptor is about to
        // redirect to /login. Keeping `loading=true` until that
        // happens avoids the empty "League not found" flash.
        return;
      }
      setLeague(leagueRes?.data?.league || null);
      setMembers(membersRes?.data?.members || []);
      setSeasons(seasonsRes?.data?.seasons || []);
      const venueCount = (venuesRes?.data?.venues || []).length;
      // For divisions/teams/games we need a season context. Pick the
      // most recent season (first in the seasons list — backend
      // returns DESC by start_date) and fetch its derived counts.
      // If no season exists yet, leave the counts at 0.
      const firstSeason = (seasonsRes?.data?.seasons || [])[0];
      if (firstSeason?.id) {
        Promise.all([
          // v2.5.124 audit — these endpoints are scoped under
          // /seasons/:seasonId/. Earlier draft of this code used
          // /leagues/:id/divisions and /leagues/:id/schedule which
          // don't exist; the divisions route lives at
          // /leagues/:id/seasons/:seasonId/divisions (leagues.js:976)
          // and games are listed via league-schedule.js mounted at
          // /leagues/:id/seasons/:seasonId/games (app.js:326,
          // league-schedule.js:630).
          api.get(`/leagues/${leagueId}/seasons/${firstSeason.id}/divisions`).catch(() => ({ data: { divisions: [] } })),
          api.get(`/leagues/${leagueId}/seasons/${firstSeason.id}/teams`).catch(() => ({ data: { teams: [] } })),
          api.get(`/leagues/${leagueId}/seasons/${firstSeason.id}/games`).catch(() => ({ data: { games: [] } })),
        ]).then(([divsRes, teamsRes, gamesRes]) => {
          if (cancelled) return;
          setProgress({
            venues:    venueCount,
            divisions: (divsRes?.data?.divisions || []).length,
            teams:     (teamsRes?.data?.teams || []).length,
            games:     (gamesRes?.data?.games || []).length,
          });
        });
      } else {
        setProgress({ venues: venueCount, divisions: 0, teams: 0, games: 0 });
      }
    }).catch((err) => {
      if (err?.response?.status === 403) {
        toast.error('You are not a member of this league');
      } else if (err?.response?.status !== 401) {
        toast.error('Could not load league');
      }
    }).finally(() => {
      // v2.5.130 — don't drop loading if auth failed; the api
      // interceptor's redirect is imminent and we want to show
      // the "Loading league…" panel until it fires, not the
      // misleading "League not found" empty state.
      if (!cancelled && !authFailedThisLoad) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [leagueId]);

  if (loading || roleLoading) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 880 }}>
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>
          Loading league…
        </div>
      </div>
    );
  }

  if (!league) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 880 }}>
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center', padding: 40 }}>
          <Trophy size={48} color="var(--text-2)" style={{ margin: '0 auto 16px', opacity: 0.5 }} />
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>League not found</div>
          <div style={{ fontSize: 13 }}>
            It may have been archived, or you may not be a member.
          </div>
        </div>
      </div>
    );
  }

  // Bucket members by role for the count display. Role badges + counts
  // give the dashboard a sense of "how the league is governed."
  const counts = {
    manager:       members.filter(m => m.member_role === 'manager').length,
    administrator: members.filter(m => m.member_role === 'administrator').length,
    viewer:        members.filter(m => m.member_role === 'viewer').length,
  };

  const activeSeason = seasons.find(s => s.status === 'active') || null;
  // 2026-09-28 — next season (status draft), e.g. registration open
  // while the current season is still running.
  const upcomingSeason = seasons
    .filter(s => s.status === 'draft')
    .sort((a, b) => String(a.start_date || '').localeCompare(String(b.start_date || '')))[0] || null;

  return (
    <div className="animate-fade-in" style={{ maxWidth: 880 }}>
      <LeagueSubNav league={league} />

      {/* Header card — league summary */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 className="section-title text-2xl" style={{ marginBottom: 6 }}>{league.name}</h1>
            <div style={{ color: 'var(--text-2)', fontSize: 13, textTransform: 'capitalize' }}>
              {league.sport}
              {league.is_public ? ' · public page enabled' : ' · private'}
              {' · '}your role: <span style={{ color: 'var(--ice)', fontWeight: 600 }}>{role || '—'}</span>
            </div>
          </div>
        </div>
        {league.description && (
          <p style={{ color: 'var(--text-2)', fontSize: 14, marginTop: 4 }}>{league.description}</p>
        )}
      </div>

      {/* Member counts */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
        <StatCard icon={Crown}  iconColor="var(--puck)"   label="Managers"       value={counts.manager} />
        <StatCard icon={Shield} iconColor="var(--ice)"    label="Administrators" value={counts.administrator} />
        <StatCard icon={Eye}    iconColor="var(--text-2)" label="Viewers"        value={counts.viewer} />
      </div>

      {/* Current season summary or empty-state. Real season-mgmt UI
          arrives in Phase 2c; this is just an at-a-glance read. */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <Calendar size={16} color="var(--ice)" />
          <span style={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Current season
          </span>
        </div>
        {activeSeason ? (
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>{activeSeason.name}</div>
            <div style={{ color: 'var(--text-2)', fontSize: 13 }}>
              {formatDateRange(activeSeason.start_date, activeSeason.end_date)}
            </div>
          </div>
        ) : (
          <div style={{ color: 'var(--text-2)', fontSize: 14 }}>
            No active season yet.
            {' '}
            {isManager && (
              <>Create one in the <strong>Seasons</strong> tab to get started.</>
            )}
          </div>
        )}
        {upcomingSeason && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)', fontSize: 13, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ color: 'var(--text-2)' }}>Next:</span>
            <strong>{upcomingSeason.name}</strong>
            {upcomingSeason.start_date && (
              <span style={{ color: 'var(--text-2)' }}>{formatDateRange(upcomingSeason.start_date, upcomingSeason.end_date)}</span>
            )}
            <Link to={`/league/${leagueId}/registration?season=${upcomingSeason.id}`} style={{ color: 'var(--ice)', textDecoration: 'none', fontWeight: 600 }}>
              Registration →
            </Link>
          </div>
        )}
      </div>

      {(role === 'manager' || role === 'administrator') && (
        <DigestCard leagueId={leagueId} initial={league.digest_frequency || 'weekly'} />
      )}

      {/* "Next steps" panel for managers — v2.5.124. Now reflects
          actual progress against the shipped feature set (Phases
          1-8). `done` is derived from counts loaded above, so as
          the manager works through the checklist the items tick
          off in place. */}
      {isManager && (
        <div className="card" style={{ borderColor: 'var(--ice-a25)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <ArrowRight size={16} color="var(--ice)" />
            <span style={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Next steps
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <NextStep
              done={members.length > 1}
              text="Invite administrators or viewers and assign permissions"
              available={true}
              sub={members.length > 1
                ? `${members.length} members`
                : 'Available now — Members tab'}
            />
            <NextStep
              done={progress.venues > 0}
              text="Add venues with weekly availability"
              available={true}
              sub={progress.venues > 0
                ? `${progress.venues} venue${progress.venues === 1 ? '' : 's'} added`
                : 'Available now — Venues tab'}
            />
            <NextStep
              done={seasons.length > 0 && progress.divisions > 0}
              text="Create your first season and add divisions"
              available={true}
              sub={seasons.length === 0
                ? 'Available now — Seasons tab'
                : progress.divisions === 0
                  ? 'Season created — now add divisions'
                  : `${seasons.length} season${seasons.length === 1 ? '' : 's'}, ${progress.divisions} division${progress.divisions === 1 ? '' : 's'}`}
            />
            <NextStep
              done={progress.teams > 0}
              text="Invite teams and allocate players"
              available={seasons.length > 0}
              sub={seasons.length === 0
                ? 'Create a season first'
                : progress.teams === 0
                  ? 'Available now — Seasons tab → Invite team'
                  : `${progress.teams} team${progress.teams === 1 ? '' : 's'} enrolled`}
            />
            <NextStep
              done={progress.games > 0}
              text="Generate a schedule and assign officials"
              available={progress.teams >= 2}
              sub={progress.teams < 2
                ? 'Need at least 2 teams enrolled first'
                : progress.games === 0
                  ? 'Available now — Schedule tab'
                  : `${progress.games} game${progress.games === 1 ? '' : 's'} scheduled`}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// 2026-09-28 — league email summary preference + "email me a sample".
function DigestCard({ leagueId, initial }) {
  const [freq, setFreq] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  useEffect(() => { setFreq(initial); }, [initial]);

  async function choose(next) {
    if (next === freq) return;
    const prev = freq;
    setFreq(next);
    setSaving(true);
    try {
      await api.patch(`/leagues/${leagueId}/me/digest`, { frequency: next });
      toast.success(next === 'off' ? 'Email summary turned off' : `You'll get a ${next} summary`);
    } catch (err) {
      setFreq(prev);
      toast.error(err?.response?.data?.error || 'Could not save');
    } finally { setSaving(false); }
  }
  async function sample() {
    setSending(true);
    try {
      await api.post(`/leagues/${leagueId}/me/digest/send-now`);
      toast.success('Sample sent — check your inbox');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not send the sample');
    } finally { setSending(false); }
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <Mail size={16} color="var(--ice)" />
        <span style={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Email summary</span>
      </div>
      <p style={{ color: 'var(--text-2)', fontSize: 13, margin: '0 0 12px' }}>
        One email with what needs you: new registrations and payments, registrations waiting to be confirmed,
        scoresheets still open after the game, games in the next week with no officials, and suspensions.
        Quiet weeks send nothing.
      </p>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {[['weekly', 'Weekly (Mondays)'], ['daily', 'Daily'], ['off', 'Off']].map(([id, text]) => (
          <button key={id} type="button" disabled={saving} onClick={() => choose(id)}
            className={freq === id ? 'btn-ice' : 'btn-ghost'} style={{ fontSize: 12, padding: '6px 12px' }}>
            {text}
          </button>
        ))}
        <button type="button" className="btn-ghost" disabled={sending} onClick={sample}
          style={{ fontSize: 12, padding: '6px 12px', marginLeft: 'auto' }}>
          {sending ? 'Sending…' : 'Email me a sample now'}
        </button>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, iconColor, label, value }) {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 8,
          background: 'var(--surface-2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={14} color={iconColor} />
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
          {label}
        </span>
      </div>
      <div className="display-num" style={{ fontSize: 28, color: 'var(--text)' }}>{value}</div>
    </div>
  );
}

function NextStep({ done, text, available, sub }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      <div style={{
        width: 18, height: 18, borderRadius: '50%',
        marginTop: 2, flexShrink: 0,
        background: done ? 'var(--goal-a20)' : (available ? 'var(--ice-a10)' : 'var(--surface-2)'),
        border: `1.5px solid ${done ? 'var(--goal)' : (available ? 'var(--ice)' : 'var(--border)')}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {done && <span style={{ color: 'var(--goal)', fontSize: 11, fontWeight: 900 }}>✓</span>}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, color: available || done ? 'var(--text)' : 'var(--text-2)' }}>
          {text}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{sub}</div>
      </div>
    </div>
  );
}
